<template>
  <SogPage title="Recherche-Mail">
    <SogCard v-show="_inquiry">
      <SogCardActions :is-toolbar="true">
        <SogCardActionButton
          v-show="_editable"
          mdi-icon="mdi-content-save"
          tooltip="Änderungen speichern"
          @click="onSave"
        />
        <SogCardActionButton
          v-show="_editable"
          mdi-icon="mdi-send-check"
          tooltip="Freigeben (Versand durch den Hintergrunddienst)"
          @click="onApprove"
        />
        <SogCardActionButton
          v-show="_editable"
          mdi-icon="mdi-delete"
          tooltip="Verwerfen"
          @click="onDiscard"
        />
        <SogCardActionButton :kind="SogButtonKind.Back" />
      </SogCardActions>
      <SogCardTitle>
        {{ _inquiry?.officeName }} · {{ _inquiry?.mailTypeText }} · {{ _inquiry?.statusText }}
      </SogCardTitle>
      <SogCardContent>
        <SogFlexColumn>
          <div
            v-show="_inquiry?.errorText"
            class="error"
          >
            {{ _inquiry?.errorText }}
          </div>
          <template v-if="_editable">
            <label class="excel-option">
              <input
                v-model="_attachExcel"
                type="checkbox"
              >
              Excel-Liste der Vorgänge beilegen
            </label>
            <SogDropDownList
              v-model="_mailType"
              placeholder="Art der Mail (ändert die Vorlage)"
              :data-source="mailTypeItems"
              :fields="_fields"
              popup-width="320px"
            />
            <SogTextBox
              v-model="_recipients"
              placeholder="Empfänger (mit ; getrennt)"
              width="100%"
            />
            <SogTextBox
              v-model="_subject"
              placeholder="Betreff"
              width="100%"
            />
            <SogTextBox
              v-model="_body"
              placeholder="Text"
              :multiline="true"
              width="100%"
            />
          </template>
          <table
            v-else
            class="readonly"
          >
            <tbody>
              <tr>
                <td>Art</td>
                <td><SogDataLabel :value="_inquiry?.mailTypeText" /></td>
              </tr>
              <tr>
                <td>Excel-Liste</td>
                <td><SogDataLabel :value="_inquiry?.attachExcel === false ? 'nein' : 'ja'" /></td>
              </tr>
              <tr>
                <td>Empfänger</td>
                <td><SogDataLabel :value="_inquiry?.recipients" /></td>
              </tr>
              <tr>
                <td>Betreff</td>
                <td><SogDataLabel :value="_inquiry?.subject" /></td>
              </tr>
              <tr>
                <td>Text</td>
                <td class="body-label"><SogDataLabel :value="_inquiry?.bodyText" /></td>
              </tr>
            </tbody>
          </table>
          <table class="items">
            <thead>
              <tr>
                <th>Kennzeichen</th>
                <th>Nummer</th>
                <th>Hinweis</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="item in _items"
                :key="item.id"
              >
                <td>{{ item.kennzeichen }}</td>
                <td>{{ item.nummer }}</td>
                <td>{{ item.isUnknown ? "angefragt" : "bereits vorhanden (zur Klärung genannt)" }}</td>
              </tr>
            </tbody>
          </table>
        </SogFlexColumn>
      </SogCardContent>
    </SogCard>
  </SogPage>
</template>

<script setup lang="ts">
import {computed, onMounted, reactive, ref, watch} from "vue";
import {useRouter} from "vue-router";
import SogPage from "@/shared/ui/SogPage.vue";
import SogCard from "@/shared/ui/SogCard.vue";
import SogCardTitle from "@/shared/ui/SogCardTitle.vue";
import SogCardContent from "@/shared/ui/SogCardContent.vue";
import SogCardActions from "@/shared/ui/SogCardActions.vue";
import SogCardActionButton from "@/shared/ui/SogCardActionButton.vue";
import SogFlexColumn from "@/shared/ui/SogFlexColumn.vue";
import SogTextBox from "@/shared/ui/SogTextBox.vue";
import SogDropDownList from "@/shared/ui/SogDropDownList.vue";
import SogDataLabel from "@/shared/ui/SogDataLabel.vue";
import {SogButtonKind} from "@/shared/ui/types/SogButtonKind";
import notificationService from "@/shared/services/NotificationService";
import apiRegistrationInquiry from "@/modules/RegistrationInquiry/api";
import VehicleRegistrationInquiry, {isInquiryEditable, VehicleRegistrationInquiryItem} from "@/shared/model/VehicleRegistrationInquiry";

interface Props {
  id?: string
}

const props = withDefaults(defineProps<Props>(), {id: undefined});

const _router = useRouter();
const _fields = {text: "value", value: "id"};
const _inquiry = ref<VehicleRegistrationInquiry | undefined>(undefined);
const _items = ref<VehicleRegistrationInquiryItem[]>([]);
const _recipients = ref("");
const _subject = ref("");
const _body = ref("");
const _mailType = ref("");
const _attachExcel = ref(true);

// Arten, die ein Entwurf haben kann (Erinnerungen entstehen nur aus dem Dienst)
const mailTypeItems = reactive([
  {id: "InsuranceCopy", value: "Versicherungsschein anfordern"},
  {id: "Urgent", value: "Dringend (Schadensfall)"},
  {id: "DoubleNumber", value: "Policennummer mehrfach"},
  {id: "ForeignPolicy", value: "Fremde Police"},
  {id: "InternalNumber", value: "Interne Nummer der Zulassungsstelle"},
]);

const _editable = computed(() => isInquiryEditable(_inquiry.value));

async function load() {
  if (!props.id) return;
  const t_res = await apiRegistrationInquiry.load(props.id);
  if (!t_res) {
    _inquiry.value = undefined;
    _items.value = [];
    return;
  }
  _inquiry.value = t_res.inquiry;
  _items.value = t_res.items;
  _recipients.value = t_res.inquiry.recipients ?? "";
  _subject.value = t_res.inquiry.subject ?? "";
  _body.value = t_res.inquiry.bodyText ?? "";
  _mailType.value = t_res.inquiry.mailTypeId ?? "";
  _attachExcel.value = t_res.inquiry.attachExcel !== false;
}

async function save(): Promise<boolean> {
  if (!_inquiry.value?.id) return false;
  return await apiRegistrationInquiry.update(_inquiry.value.id, _recipients.value, _subject.value, _body.value, _attachExcel.value);
}

async function onSave() {
  if (await save()) {
    notificationService.success("Änderungen wurden gespeichert");
    await load();
  }
}

function onApprove() {
  const t_id = _inquiry.value?.id;
  if (!t_id) return;
  notificationService.showYesNo(
    "Mail freigeben",
    "Soll die Mail an " + _recipients.value + " freigegeben werden? Sie wird danach automatisch versendet.",
    async () => {
      if (_editable.value && !(await save())) return;
      const t_res = await apiRegistrationInquiry.approve(t_id);
      if (t_res.successful) {
        notificationService.success("Mail wurde freigegeben" + (t_res.message ? ". " + t_res.message : ""));
      } else if (t_res.message?.startsWith("Alle angefragten")) {
        notificationService.success(t_res.message);
      }
      await _router.push("/registrationinquiry/list");
    });
}

function onDiscard() {
  const t_id = _inquiry.value?.id;
  if (!t_id) return;
  notificationService.showYesNo(
    "Mail verwerfen",
    "Soll der Entwurf verworfen werden? Die Zulassungen werden danach 30 Tage lang nicht erneut angefragt.",
    async () => {
      if (await apiRegistrationInquiry.discard(t_id)) {
        notificationService.success("Entwurf wurde verworfen");
        await _router.push("/registrationinquiry/list");
      }
    });
}

// Art gewählt: vor der Bestätigung die bisherige Art zeigen, nach „Ja" die Vorlage der neuen Art übernehmen
watch(_mailType, (newType) => {
  const t_inquiry = _inquiry.value;
  if (!t_inquiry?.id || !_editable.value || newType === t_inquiry.mailTypeId) return;
  _mailType.value = t_inquiry.mailTypeId ?? "";
  const t_label = mailTypeItems.find(t => t.id === newType)?.value ?? newType;
  notificationService.showYesNo(
    "Art ändern",
    `Betreff und Text werden durch die Vorlage der Art „${t_label}“ ersetzt. Manuelle Änderungen am Text gehen verloren. Fortfahren?`,
    async () => {
      const t_ok = await apiRegistrationInquiry.changeMailType(t_inquiry.id ?? "", newType);
      if (t_ok) {
        notificationService.success("Art wurde geändert");
        await load();
      }
    });
});

watch(() => props.id, load);

onMounted(load);
</script>

<style scoped>
.error {
  color: var(--sog-error-color, #c62828);
  margin-bottom: 8px;
}

.items {
  border-collapse: collapse;
}

.items th,
.items td {
  padding: 4px 16px 4px 0;
  text-align: left;
  vertical-align: middle;
}

.items td {
  font-family: monospace;
}

.readonly td {
  padding: 4px 16px 4px 0;
  vertical-align: top;
}

.readonly td:first-child {
  color: var(--sog-muted-color, #777);
  white-space: nowrap;
}

.body-label {
  white-space: pre-wrap;
}
</style>
